import { Avatar, SectionHead } from '@/components/ui';
import { formatBRL, formatBRLShort, formatPercent, LEVERAGE_LONG_LABELS, MESES } from '@/lib/format';
import type { CommissionSummary, CommissionTier, LeverageType } from '@/lib/types';
import './commission.css';

interface CommissionCardProps {
  commission: CommissionSummary;
  year: number;
  /** Painel de uma pessoa: faixa e distância até a próxima; na operação, a lista por membro. */
  personal: boolean;
  leverageType: LeverageType | null;
  /** O mês escolhido, para o seletor não voltar ao anterior enquanto o novo carrega. */
  selectedMonth?: number;
  onMonthChange: (month: number) => void;
}

const tierRange = (tier: CommissionTier) => {
  if (tier.from === 0 && tier.to !== null) return `Até ${formatBRLShort(tier.to)}`;
  if (tier.to === null) return `Acima de ${formatBRLShort(tier.from)}`;
  return `${formatBRLShort(tier.from)} a ${formatBRLShort(tier.to)}`;
};

/** Quanto do degrau já foi percorrido (0-1). A última faixa, sem teto, fica cheia quando alcançada. */
function stepFill(tier: CommissionTier, billing: number): number {
  if (billing <= tier.from && tier.from > 0) return 0;
  if (tier.to === null) return 1;
  return Math.min(1, (billing - tier.from) / (tier.to - tier.from));
}

/**
 * Comissão individual por faixa de faturamento no mês. Segue os filtros do
 * painel (ano, membro e tipo) e tem o próprio seletor de mês.
 */
export function CommissionCard({ commission, year, personal, leverageType, selectedMonth, onMonthChange }: CommissionCardProps) {
  const { month, tiers } = commission;
  const current = tiers.find((tier) => tier.key === month.tier) ?? null;
  const next = month.next ? tiers.find((tier) => tier.key === month.next?.key) : null;

  return (
    <section className="card commission" aria-labelledby="commission-title">
      <div className="commission-head">
        <SectionHead title={<span id="commission-title">Comissão</span>} />
        <select
          className="inp commission-month"
          value={selectedMonth ?? month.month}
          onChange={(event) => onMonthChange(Number(event.target.value))}
          aria-label="Mês da comissão"
        >
          {MESES.map((label, index) => <option key={label} value={index + 1}>{label}</option>)}
        </select>
      </div>

      <div className="commission-body">
        <div className="commission-figures">
          <div className="muted commission-caption">
            {personal ? 'Sua comissão' : 'Comissão da operação'} em {month.label.toLowerCase()} de {year}
          </div>
          <div className="display num commission-value">{formatBRL(month.commission)}</div>
          <div className="commission-meta">
            {current ? (
              <span className="chip chip-primary" data-tier={current.key}>
                {current.label} · {formatPercent(current.rate)}
              </span>
            ) : null}
            <span className="muted">
              Faturamento do mês <strong className="num">{formatBRLShort(month.billing)}</strong>
            </span>
          </div>
          <div className="commission-year">
            <span className="label">No ano</span>
            <span className="display num">{formatBRLShort(commission.yearTotal)}</span>
          </div>
          {leverageType ? (
            <p className="muted commission-note">
              Só {LEVERAGE_LONG_LABELS[leverageType]?.toLowerCase()}: a faixa segue o faturamento do mês inteiro, e a taxa
              vale sobre {formatBRLShort(month.base)} desse tipo.
            </p>
          ) : null}
        </div>

        <div className="commission-side">
          {personal ? (
            <TierLadder tiers={tiers} billing={month.billing} activeKey={month.tier} />
          ) : (
            <MemberCommissions commission={commission} />
          )}
          {personal && next && month.next ? (
            <p className="commission-next">
              {month.next.missing > 0 ? (
                <>
                  Faltam <strong className="num">{formatBRL(month.next.missing)}</strong> para passar de{' '}
                  {formatBRLShort(next.from)} e entrar no {next.label} ({formatPercent(next.rate)}).
                </>
              ) : (
                <>No teto da faixa: qualquer venda a mais já leva o mês ao {next.label} ({formatPercent(next.rate)}).</>
              )}
            </p>
          ) : null}
          {personal && month.tier && !next ? (
            <p className="commission-next is-top">Faixa máxima alcançada no mês.</p>
          ) : null}
          {!personal ? <TierLegend tiers={tiers} /> : null}
        </div>
      </div>
    </section>
  );
}

/** As três faixas como degraus, com o faturamento do mês preenchendo cada um. */
function TierLadder({ tiers, billing, activeKey }: { tiers: CommissionTier[]; billing: number; activeKey: string | null }) {
  return (
    <ol className="tier-ladder" aria-label="Faixas de comissão individuais">
      {tiers.map((tier) => (
        <li key={tier.key} className="tier-step" data-active={tier.key === activeKey || undefined}>
          <div className="tier-step-head">
            <span className="tier-step-name">{tier.label}</span>
            <span className="display num tier-step-rate">{formatPercent(tier.rate)}</span>
          </div>
          <div className="bar-track tier-step-track">
            <div className="bar-fill" style={{ width: `${stepFill(tier, billing) * 100}%` }} />
          </div>
          <span className="muted num tier-step-range">{tierRange(tier)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Na operação, a tabela das faixas fica como legenda compacta. */
function TierLegend({ tiers }: { tiers: CommissionTier[] }) {
  return (
    <ul className="tier-legend" aria-label="Faixas de comissão individuais">
      {tiers.map((tier) => (
        <li key={tier.key}>
          <strong>{tier.label}</strong> <span className="muted num">{tierRange(tier)}</span>{' '}
          <span className="num">{formatPercent(tier.rate)}</span>
        </li>
      ))}
    </ul>
  );
}

function MemberCommissions({ commission }: { commission: CommissionSummary }) {
  const { members, tiers } = commission;
  if (members.length === 0) return <div className="empty">Ninguém faturou neste mês.</div>;
  const labelOf = (key: string) => tiers.find((tier) => tier.key === key)?.label ?? key;
  return (
    <ul className="commission-members">
      {members.map((member) => (
        <li key={member.id} className="commission-member">
          <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={26} />
          <span className="truncate commission-member-name">{member.name}</span>
          <span className="chip" data-tier={member.tier} title={`${formatPercent(member.rate)} sobre o faturamento`}>
            {labelOf(member.tier)}
          </span>
          <span className="muted num nowrap commission-member-billing">{formatBRLShort(member.billing)}</span>
          <span className="num nowrap emphasis commission-member-value">{formatBRLShort(member.commission)}</span>
        </li>
      ))}
    </ul>
  );
}
